import React, { useState } from "react";
import { useQuery, useLazyQuery, useMutation, gql } from "@apollo/client";

// Shown in the approval modal: what approving this leave does to the
// roster (minimum-staffing check) and which of the employee's bookings fall
// inside it, with a way to hand each one to someone else or let the
// customer pick a new slot.

const GET_APPROVAL_CONTEXT = gql`
  query GetLeaveApprovalContext($leaveId: ID!) {
    getLeaveApprovalContext(leaveId: $leaveId) {
      status
      message
      staffing {
        enabled
        enforcement
        gender
        required
        poolSize
        shortDays {
          date
          available
          required
        }
      }
      conflictingBookings {
        id
        bookingId
        bookingDate
        timeSlot
        status
        totalTime
        femaleTimeSlot
        address
        customerName
        rescheduleAllowed
      }
    }
  }
`;

const GET_AVAILABLE_EMPLOYEES = gql`
  query GetAvailableEmployeesForLeave($data: getAvailableEmployeeForSlot!) {
    getAvailableEmployeesForSlot(data: $data) {
      status
      message
      employees {
        id
        fullName
      }
    }
  }
`;

const REASSIGN_BOOKING = gql`
  mutation ReassignBookingForLeave($data: ReassignBookingInput!) {
    reassignBooking(data: $data) {
      status
      message
    }
  }
`;

const ALLOW_RESCHEDULE = gql`
  mutation AllowRescheduleForLeave($data: SetRescheduleAllowedInput!) {
    adminSetBookingRescheduleAllowed(data: $data) {
      status
      message
      rescheduleAllowed
    }
  }
`;

const istParts = (value) => {
  const d = new Date(value);
  const date = d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  const time = d.toLocaleTimeString("en-GB", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  const label = d.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
  return { date, time, label };
};

const formatDay = (date) =>
  new Date(`${date}T00:00:00`).toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });

const ConflictRow = ({ booking, leaveId, onChanged }) => {
  const [picking, setPicking] = useState(false);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [loadEmployees, { data: empData, loading: empLoading }] = useLazyQuery(
    GET_AVAILABLE_EMPLOYEES,
    { fetchPolicy: "network-only" },
  );
  const [reassign] = useMutation(REASSIGN_BOOKING);
  const [allowReschedule] = useMutation(ALLOW_RESCHEDULE);

  const { date, time, label } = istParts(booking.bookingDate);
  const candidates = empData?.getAvailableEmployeesForSlot?.employees || [];

  const startPicking = () => {
    setPicking(true);
    setError("");
    loadEmployees({
      variables: {
        data: {
          femaleTimeSlot: booking.femaleTimeSlot,
          date,
          timeSlot: time,
          address:
            typeof booking.address === "string"
              ? booking.address
              : JSON.stringify(booking.address || ""),
          serviceTime: parseInt(booking.totalTime, 10) || 60,
        },
      },
    });
  };

  const run = async (fn, key) => {
    setBusy(true);
    setError("");
    try {
      const res = await fn();
      const result = res.data?.[key];
      if (result?.status !== "SUCCESS") {
        throw new Error(result?.message || "Request failed");
      }
      setPicking(false);
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="py-2 border-b last:border-b-0">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-sm font-medium text-gray-800">
            {label} · {booking.customerName || "Customer"}
          </div>
          <div className="text-xs text-gray-500">
            #{booking.bookingId || booking.id} · {booking.status}
            {booking.rescheduleAllowed && " · reschedule offered"}
          </div>
        </div>
        {!picking && (
          <div className="flex gap-2 shrink-0">
            <button
              type="button"
              onClick={startPicking}
              className="text-xs px-2 py-1 rounded border bg-white hover:bg-gray-100"
            >
              Reassign
            </button>
            {!booking.rescheduleAllowed && (
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  run(
                    () =>
                      allowReschedule({
                        variables: {
                          data: {
                            orderId: booking.id,
                            allowed: true,
                            note: `Professional on leave (leave ${leaveId})`,
                          },
                        },
                      }),
                    "adminSetBookingRescheduleAllowed",
                  )
                }
                className="text-xs px-2 py-1 rounded border bg-white hover:bg-gray-100 disabled:opacity-40"
              >
                Let customer reschedule
              </button>
            )}
          </div>
        )}
      </div>
      {picking && (
        <div className="mt-2 flex items-center gap-2">
          <select
            className="flex-1 border p-1 rounded text-sm bg-white"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            disabled={empLoading}
          >
            <option value="">
              {empLoading
                ? "Finding free professionals..."
                : candidates.length
                  ? "Pick a professional"
                  : "No one free for this slot"}
            </option>
            {candidates.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.fullName}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={!selected || busy}
            onClick={() =>
              run(
                () =>
                  reassign({
                    variables: {
                      data: { orderId: booking.id, employeeId: selected },
                    },
                  }),
                "reassignBooking",
              )
            }
            className="text-xs px-2 py-1 rounded bg-blue-600 text-white disabled:opacity-40"
          >
            Assign
          </button>
          <button
            type="button"
            onClick={() => setPicking(false)}
            className="text-xs px-2 py-1 rounded border bg-white"
          >
            Cancel
          </button>
        </div>
      )}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </li>
  );
};

const LeaveConflictsPanel = ({ leaveId }) => {
  const { data, loading, refetch } = useQuery(GET_APPROVAL_CONTEXT, {
    variables: { leaveId },
    fetchPolicy: "network-only",
  });

  if (loading) {
    return (
      <div className="mb-4 p-3 rounded border bg-gray-50 text-sm text-gray-500">
        Checking staffing and bookings...
      </div>
    );
  }

  const ctx = data?.getLeaveApprovalContext;
  if (!ctx || ctx.status !== "SUCCESS") {
    return (
      <div className="mb-4 p-3 rounded border bg-gray-50 text-sm text-gray-500">
        Could not check staffing and bookings{ctx?.message ? `: ${ctx.message}` : ""}.
      </div>
    );
  }

  const { staffing, conflictingBookings = [] } = ctx;
  const short = staffing?.enabled ? staffing.shortDays : [];
  const blocking = staffing?.enforcement === "BLOCK";

  return (
    <div className="mb-4 space-y-3">
      {short.length > 0 && (
        <div
          className={`p-3 rounded border ${
            blocking
              ? "bg-red-50 border-red-200 text-red-800"
              : "bg-amber-50 border-amber-200 text-amber-800"
          }`}
        >
          <p className="text-sm font-semibold">
            {blocking ? "Approval blocked: " : "Staffing warning: "}
            fewer than {staffing.required}{" "}
            {String(staffing.gender || "").toLowerCase()} professionals left
          </p>
          <p className="text-xs mt-1">
            {short
              .map((d) => `${formatDay(d.date)}: ${d.available} available`)
              .join(" · ")}
          </p>
        </div>
      )}

      <div className="p-3 rounded border bg-gray-50">
        <p className="text-sm font-semibold text-gray-700">
          {conflictingBookings.length === 0
            ? "No bookings during this leave"
            : `${conflictingBookings.length} booking${
                conflictingBookings.length === 1 ? "" : "s"
              } during this leave`}
        </p>
        {conflictingBookings.length > 0 && (
          <ul className="mt-1">
            {conflictingBookings.map((b) => (
              <ConflictRow
                key={b.id}
                booking={b}
                leaveId={leaveId}
                onChanged={() => refetch()}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default LeaveConflictsPanel;
